import { partition } from "lodash";

import { calciteTypeToChartDataType } from "../chart/hql/chartHqlUtils.js";
import { ChartDataType } from "../chart/types.js";
import { ColumnFilter } from "../display-table/filterTypes.js";
import { CalciteType, HqlAggregationFunction } from "../hql/types.js";
import { SemanticDatasetName } from "../idTypeBrands.js";
import { type HexSLTypes } from "../index.js";
import {
  semanticDatasetTitleString,
  semanticDimensionTitleString,
  semanticMeasureTitleString,
} from "../semantic-layer/semanticTitleUtils.js";
import {
  type SemanticViewHydratedContentGroup,
  getSemanticViewHydratedContents,
} from "../semantic-layer/semanticViewUtils/hydrateContents.js";
import { concatRespectingCase } from "../utils/stringUtils.js";

import {
  CHART_FOLD_KEYS,
  COUNT_STAR_ARG,
  COUNT_STAR_LABEL,
} from "./exploreConstants.js";
import { ExploreInputField } from "./exploreDndTypes.js";
import { hqlAggToPivotAgg } from "./explorePivotUtils.js";
import { HexSLTypeMapping, NumericMeasureAggTypes } from "./exploreUtils.js";
import {
  SemanticAwareColumn,
  SemanticAwareFieldGroup,
} from "./semanticTypes.js";
import {
  ExploreField,
  ExploreFieldType,
  getExploreTimeUnitName,
} from "./types.js";

/**
 * A field is a aggregated if (1) it is a measure or (2) it is a
 * dimension/column that has an aggregation set
 */
export function isAggregatedField(field: ExploreField): boolean {
  if (field.fieldType === ExploreFieldType.MEASURE) {
    return true;
  }
  return field.aggregation != null;
}

export function getFieldActiveScaleType(field: ExploreField): ChartDataType {
  return field.scaleType ?? calciteTypeToChartDataType(field.dataType);
}

export const BASE_COUNT_STAR_INPUT_FIELD: ExploreInputField = {
  id: COUNT_STAR_ARG,
  name: COUNT_STAR_LABEL,
  type: "NUMBER",
  fieldType: "MEASURE",
  queryPath: [],
};

export const BASE_COUNT_STAR_FIELD: Omit<SemanticAwareColumn, "queryPath"> = {
  columnId: COUNT_STAR_ARG,
  columnName: COUNT_STAR_LABEL,
  columnType: "NUMBER",
  fieldType: ExploreFieldType.MEASURE,
  description: undefined,
};

export const getCountStarField = (
  queryPath: SemanticDatasetName[],
): SemanticAwareColumn => ({
  ...BASE_COUNT_STAR_FIELD,
  queryPath,
});

export const getChartFoldKeysField = (
  label: string | undefined,
): SemanticAwareColumn => ({
  columnId: CHART_FOLD_KEYS,
  columnName: label ?? "Fields",
  columnType: "STRING",
  fieldType: ExploreFieldType.DIMENSION,
  description: undefined,
  queryPath: [],
});

export const isCountStarOutputColumn = (columnId: string) => {
  return columnId.toLowerCase().endsWith(COUNT_STAR_ARG.toLowerCase());
};

export interface PartialInputField {
  id: string;
  queryPath: SemanticDatasetName[] | undefined;
}

/**
 * Given an explore field, generates a unique column identifier for the field,
 * which includes the query path if it exists.
 */
export function generateColumnIdForField(
  field:
    | Pick<ExploreField, "value" | "queryPath">
    | PartialInputField
    | SemanticAwareColumn
    | ColumnFilter,
): string {
  const queryPath = field.queryPath;
  const value =
    "value" in field
      ? field.value
      : "columnId" in field
        ? field.columnId
        : "column" in field
          ? field.column
          : field.id;
  // the first item in the query path is always the base dataset
  // so having only 1 item in the query path doesn't point to any joins
  if (queryPath == null || queryPath.length === 0) {
    return value;
  }
  return [...queryPath, value].join(":");
}

/**
 * This generates a field name that is used to identify fields in the query
 * results. This is used across the viz types to keep column names consistent.
 */
export function getExploreFieldName(
  field: Pick<
    ExploreField,
    "aggregation" | "truncUnit" | "value" | "queryPath" | "fieldType"
  >,
): string {
  let value = generateColumnIdForField(field);

  if (field.fieldType === ExploreFieldType.MEASURE) {
    return value;
  }
  if (field.aggregation != null) {
    value = concatRespectingCase(
      value,
      // use pivot agg to stay in sync with the pivot column name
      `_${hqlAggToPivotAgg(field.aggregation) ?? field.aggregation}`,
      "append",
    );
  }
  if (field.truncUnit != null) {
    const timeUnitName = getExploreTimeUnitName(field.truncUnit, undefined);
    value = concatRespectingCase(value, `_${timeUnitName}`, "append");
  }

  return value;
}

export const EXPLORE_CHART_ONLY_AGGREGATIONS: Set<HqlAggregationFunction> =
  new Set(["StdDev", "StdDevPop", "Variance", "VariancePop"]);

export function partitionNestedTableFields(fields: ExploreField[]): {
  aggFields: ExploreField[];
  groupByFields: ExploreField[];
} {
  const [aggFields, groupByFields] = partition(
    fields,
    (field) => field.aggregation != null || field.fieldType === "MEASURE",
  );

  return {
    aggFields,
    groupByFields,
  };
}

export function calciteTypeisDatetimeType(dataType: CalciteType): boolean {
  return calciteTypeToChartDataType(dataType) === "datetime";
}

export function calciteTypeisNumericType(dataType: CalciteType): boolean {
  return calciteTypeToChartDataType(dataType) === "number";
}

export const measureToExploreInputField = (
  measure: HexSLTypes.Measure,
  queryPath: SemanticDatasetName[],
  originalSemanticDatasetName: SemanticDatasetName,
  viewContentGroupItem?: Pick<
    HexSLTypes.ViewContentGroupItem,
    "title" | "description"
  >,
): SemanticAwareColumn => ({
  measure,
  columnName:
    viewContentGroupItem?.title ?? semanticMeasureTitleString(measure),
  columnType: NumericMeasureAggTypes.guard(measure.type)
    ? "NUMBER"
    : HexSLTypeMapping[measure.type],
  columnId: measure.name,
  fieldType: ExploreFieldType.MEASURE,
  description:
    viewContentGroupItem?.description ?? measure.description ?? undefined,
  queryPath,
  originalSemanticDatasetName,
});

export const dimensionToExploreInputField = (
  dimension: HexSLTypes.Dimension,
  queryPath: SemanticDatasetName[],
  originalSemanticDatasetName: SemanticDatasetName,
  viewContentGroupItem?: Pick<
    HexSLTypes.ViewContentGroupItem,
    "title" | "description"
  >,
): SemanticAwareColumn => ({
  dimension,
  columnName:
    viewContentGroupItem?.title ?? semanticDimensionTitleString(dimension),
  // The dimensions we pass in here _should_ always have a `type` since they aren't
  // from user-defined calcs (which is the only case where we elide the type).
  columnType: HexSLTypeMapping[dimension.type ?? "null"],
  columnId: dimension.name,
  fieldType: ExploreFieldType.DIMENSION,
  description:
    viewContentGroupItem?.description ?? dimension.description ?? undefined,
  queryPath,
  originalSemanticDatasetName,
});

export const semanticDatasetToFieldGroup = ({
  dimensions,
  includeCountStarField = false,
  measures,
  name,
  originalSemanticDatasetName,
  queryPath,
  title,
}: {
  name: string;
  title: string | null;
  measures: HexSLTypes.Measure[];
  includeCountStarField?: boolean;
  dimensions: HexSLTypes.Dimension[];
  queryPath: SemanticDatasetName[];
  originalSemanticDatasetName: SemanticDatasetName;
}): SemanticAwareFieldGroup => {
  return {
    name,
    title: semanticDatasetTitleString({ name, title }),
    type: "dataset",
    columns: dimensions.flatMap((dim) =>
      dim.hidden
        ? []
        : [
            dimensionToExploreInputField(
              dim,
              queryPath,
              originalSemanticDatasetName,
            ),
          ],
    ),
    measures: [
      ...(includeCountStarField ? [getCountStarField(queryPath)] : []),
      ...measures.flatMap((measure) =>
        measure.hidden
          ? []
          : [
              measureToExploreInputField(
                measure,
                queryPath,
                originalSemanticDatasetName,
              ),
            ],
      ),
    ],
    children: [],
  };
};

type ViewDatasetInfo = Pick<
  HexSLTypes.Dataset,
  "name" | "title" | "dimensions" | "measures"
>;

export const semanticViewToFieldGroups = ({
  contents,
  datasets,
}: {
  contents: HexSLTypes.ViewContents;
  datasets: readonly ViewDatasetInfo[];
}): SemanticAwareFieldGroup[] => {
  const hydratedContents = getSemanticViewHydratedContents({
    contents,
    datasets,
  });

  const convertHydratedGroupToFieldGroup = (
    hydratedGroup: SemanticViewHydratedContentGroup,
  ): SemanticAwareFieldGroup => {
    const columns = hydratedGroup.dimensions.map((d) =>
      dimensionToExploreInputField(
        d.dimension,
        d.queryPath as SemanticDatasetName[],
        d.queryPathCanonicalDatasetName,
        d,
      ),
    );

    const measures = hydratedGroup.measures.map((m) =>
      measureToExploreInputField(
        m.measure,
        m.queryPath as SemanticDatasetName[],
        m.queryPathCanonicalDatasetName,
        m,
      ),
    );

    return {
      name: hydratedGroup.dataset.name,
      title: hydratedGroup.title,
      type: "dataset",
      columns,
      measures,
      children: hydratedGroup.contents.map(convertHydratedGroupToFieldGroup),
    };
  };

  return hydratedContents.map(convertHydratedGroupToFieldGroup);
};

export const findMatchingSemanticAwareColumn = (
  fieldGroups: SemanticAwareFieldGroup[],
  type: "columns" | "measures",
  matcher: (column: SemanticAwareColumn) => boolean,
): {
  column: SemanticAwareColumn;
  fieldGroup: SemanticAwareFieldGroup;
  fieldGroupIndex: number;
} | null => {
  for (const [fieldGroupIndex, fieldGroup] of fieldGroups.entries()) {
    for (const column of fieldGroup[type]) {
      if (matcher(column)) {
        return { column, fieldGroup, fieldGroupIndex };
      }
    }
  }
  return null;
};

/**
 * Temporary mapping utility until we can deprecate the `ExploreInputField`
 */
export const semanticAwareColumnToExploreInputField = (
  col: SemanticAwareColumn,
): ExploreInputField => {
  if (col.columnId === COUNT_STAR_ARG) {
    return {
      id: COUNT_STAR_ARG,
      name: COUNT_STAR_LABEL,
      type: col.columnType,
      fieldType: col.fieldType,
      queryPath: col.queryPath,
      originalSemanticDatasetName: col.originalSemanticDatasetName,
    };
  }
  return {
    id: col.columnId,
    name: col.columnName,
    type: col.columnType,
    fieldType: col.fieldType,
    queryPath: col.queryPath,
    aggregation: col.columnAggregation,
    originalSemanticDatasetName: col.originalSemanticDatasetName,
  };
};

export const exploreInputFieldToSemanticAwareColumn = (
  col: ExploreInputField,
): SemanticAwareColumn => {
  return {
    columnId: col.id,
    columnName: col.name,
    columnType: col.type,
    fieldType: col.fieldType,
    queryPath: col.queryPath,
    description: col.description,
    columnAggregation: col.aggregation,
    originalSemanticDatasetName: col.originalSemanticDatasetName,
  };
};
